import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { updateLastActive } from '@/lib/activityTracker';

export interface CompanyData {
  companyName: string;
  companyCity: string;
  companyProvince: string;
  companyKvk?: string;
  companyWebsite?: string;
  companyPhone?: string;
  newsletterOptIn?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  rolesLoaded: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isMSP: boolean;
  signUp: (email: string, password: string, fullName: string, companyData?: CompanyData, inviteToken?: string) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isMSP, setIsMSP] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const attemptInviteAccept = async (session: { access_token: string; user: { id: string; email?: string } }) => {
    const pendingToken = sessionStorage.getItem('msp_invite_token');
    if (!pendingToken) return;

    console.log('[AuthProvider] Pending invite token found, calling accept-msp-invite');
    try {
      const { data } = await supabase.functions.invoke('accept-msp-invite', {
        body: { token: pendingToken },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (data?.success) {
        console.log('[AuthProvider] Invite accepted, msp_id:', data.msp_id);
        sessionStorage.removeItem('msp_invite_token');
        sessionStorage.removeItem('msp_invite_email');
        sessionStorage.removeItem('msp_invite_msp_name');
      } else if (data?.invite_email) {
        // Email mismatch — keep token so user can retry with correct account
        console.warn('[AuthProvider] Invite email mismatch:', data.invite_email, 'vs', session.user.email);
      } else {
        // Expired, revoked, or other error — clear token
        console.warn('[AuthProvider] Invite accept failed:', data?.error);
        sessionStorage.removeItem('msp_invite_token');
      }
    } catch (err) {
      console.error('[AuthProvider] Invite accept error:', err);
    }
  };

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | undefined;
    let cancelled = false;
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const init = () => {
      if (cancelled) return;

      // Set up auth state listener FIRST
      const { data } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          setSession(session);
          setUser(session?.user ?? null);

          // Defer role checking to avoid deadlock
          if (session?.user) {
            setRolesLoaded(false);
            setTimeout(() => {
              checkUserRoles(session.user.id);
              updateLastActive(session.user.id);
            }, 0);

            // Auto-accept pending invite on sign-in (belt-and-suspenders for all flows)
            if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
              await attemptInviteAccept(session);
            }
          } else {
            setIsAdmin(false);
            setIsSuperAdmin(false);
            setIsMSP(false);
            setRolesLoaded(true);
          }
        }
      );
      subscription = data.subscription;

      // THEN check for existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setRolesLoaded(false);
          checkUserRoles(session.user.id);
        } else {
          setRolesLoaded(true);
        }
        setLoading(false);
      });
    };

    // Defer auth init off the critical render path so public marketing/academy
    // pages get a faster first paint. Auth-protected routes still receive the
    // session before any redirect/guards run because they typically wait on `loading`.
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleHandle = (window as any).requestIdleCallback(init, { timeout: 1500 });
    } else {
      timeoutHandle = setTimeout(init, 0);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== undefined && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      subscription?.unsubscribe();
    };
  }, []);

  const checkUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (!error && data) {
        const hasSuperAdmin = data.some(r => r.role === 'super_admin');
        setIsSuperAdmin(hasSuperAdmin);
        setIsAdmin(hasSuperAdmin || data.some(r => r.role === 'admin'));
        const hasMspRole = data.some(r => r.role === 'msp');
        setIsMSP(hasMspRole);

        // Check msp_users membership (for invited team members without 'msp' user_role)
        if (!hasMspRole) {
          const { data: mspMembership } = await supabase
            .from('msp_users')
            .select('id')
            .eq('user_id', userId)
            .eq('status', 'active')
            .limit(1);

          if (mspMembership && mspMembership.length > 0) {
            setIsMSP(true);
          }
        }
      }
    } catch (err) {
      console.error('Error checking roles:', err);
    } finally {
      setRolesLoaded(true);
    }
  };

  const signUp = async (email: string, password: string, fullName: string, companyData?: CompanyData, inviteToken?: string) => {
    // Redirect MSP registrations to dashboard profile page with welcome flag
    // For invite registrations, redirect back to login with the invite token
    const redirectUrl = companyData 
      ? `${window.location.origin}/dashboard/profiel?welcome=true`
      : inviteToken
        ? `${window.location.origin}/auth/login?invite_token=${inviteToken}`
        : `${window.location.origin}/`;
    
    const metadata: Record<string, string> = {
      full_name: fullName,
    };

    // Add company data if provided
    if (companyData) {
      metadata.company_name = companyData.companyName;
      metadata.company_city = companyData.companyCity;
      metadata.company_province = companyData.companyProvince;
      if (companyData.companyKvk) metadata.company_kvk = companyData.companyKvk;
      if (companyData.companyWebsite) metadata.company_website = companyData.companyWebsite;
      if (companyData.companyPhone) metadata.company_phone = companyData.companyPhone;
      if (companyData.newsletterOptIn) metadata.newsletter_opt_in = 'true';
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) {
      // Check if account is disabled
      const { data: { user: signedInUser } } = await supabase.auth.getUser();
      if (signedInUser) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_disabled')
          .eq('id', signedInUser.id)
          .single();
        if ((profile as any)?.is_disabled) {
          await supabase.auth.signOut();
          return { error: { message: 'Uw account is uitgeschakeld. Neem contact op met de beheerder.' } };
        }
      }
    }
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsMSP(false);
    setRolesLoaded(true);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      rolesLoaded,
      isAdmin,
      isSuperAdmin,
      isMSP,
      signUp,
      signIn,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}